import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { CircleMarker, MapContainer, Polyline, TileLayer, useMap } from 'react-leaflet'
import type { LatLngBoundsExpression, LatLngExpression } from 'leaflet'
import 'leaflet/dist/leaflet.css'

import type { WorkoutGpsTrace } from '../../lib/api/adminMobileUsers'
import { downloadCsv } from '../../utils/csv'
import { Button } from '../ui/button'
import {
  TRACE_ASCENT_COLOR,
  TRACE_DESCENT_COLOR,
  TRACE_FLAT_COLOR,
  TRACE_UNKNOWN_ELEVATION_COLOR,
  buildElevationTraceSegments,
  buildWorkoutGpsSampleInfos,
  buildWorkoutGpsTraceCsv
} from './workoutGpsTraceUtils'

const REJECTED_COLOR = '#ef4444'

type Props = {
  trace: WorkoutGpsTrace
}

const formatTimestamp = (ts: number) => {
  if (!Number.isFinite(ts) || ts <= 0) return '—'
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    }).format(new Date(ts))
  } catch {
    return String(ts)
  }
}

const FitBounds = ({ bounds }: { bounds: LatLngBoundsExpression | null }) => {
  const map = useMap()
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [24, 24] })
    }
  }, [map, bounds])
  return null
}

const PanTo = ({ position }: { position: LatLngExpression | null }) => {
  const map = useMap()
  useEffect(() => {
    if (position) {
      map.panTo(position, { animate: true })
    }
  }, [map, position])
  return null
}

export const WorkoutGpsDebugView = ({ trace }: Props) => {
  const samples = trace.samples
  const sampleInfos = useMemo(
    () => buildWorkoutGpsSampleInfos(samples),
    [samples]
  )
  const [cursor, setCursor] = useState<{ workoutId: string; index: number }>({
    workoutId: trace.workoutId,
    index: 0
  })
  const selectedIndex = cursor.workoutId === trace.workoutId ? cursor.index : 0
  const setSelectedIndex = (index: number | ((prev: number) => number)) => {
    setCursor((prev) => {
      const base = prev.workoutId === trace.workoutId ? prev.index : 0
      const next = typeof index === 'function' ? index(base) : index
      return { workoutId: trace.workoutId, index: next }
    })
  }
  const [showRejected, setShowRejected] = useState(false)
  const [followCursor, setFollowCursor] = useState(false)

  const rejectedSamples = useMemo(
    () => samples.filter((s) => !s.accepted),
    [samples]
  )

  const segments = useMemo(
    () => buildElevationTraceSegments(samples),
    [samples]
  )

  const bounds = useMemo<LatLngBoundsExpression | null>(() => {
    if (samples.length === 0) return null
    let minLat = Infinity
    let minLng = Infinity
    let maxLat = -Infinity
    let maxLng = -Infinity
    for (const s of samples) {
      if (s.latitude < minLat) minLat = s.latitude
      if (s.latitude > maxLat) maxLat = s.latitude
      if (s.longitude < minLng) minLng = s.longitude
      if (s.longitude > maxLng) maxLng = s.longitude
    }
    if (!Number.isFinite(minLat) || !Number.isFinite(minLng)) return null
    return [
      [minLat, minLng],
      [maxLat, maxLng]
    ]
  }, [samples])

  const initialCenter = useMemo<LatLngExpression>(() => {
    if (samples.length === 0) return [0, 0]
    return [samples[0].latitude, samples[0].longitude]
  }, [samples])

  const selected = samples[selectedIndex]
  const selectedInfo = sampleInfos[selectedIndex]
  const cursorPosition: LatLngExpression | null = selected
    ? [selected.latitude, selected.longitude]
    : null

  const sliderRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.value = String(selectedIndex)
    }
  }, [selectedIndex])

  const counts = useMemo(() => {
    let running = 0
    let paused = 0
    let accuracy = 0
    let speed = 0
    let duplicate = 0
    for (const s of samples) {
      if (!s.accepted) {
        if (s.rejectedReason === 'paused') paused += 1
        else if (s.rejectedReason === 'accuracy') accuracy += 1
        else if (s.rejectedReason === 'impossible_speed') speed += 1
        else if (s.rejectedReason === 'duplicate') duplicate += 1
        continue
      }
      if (s.state === 'paused') paused += 1
      else running += 1
    }
    return { running, paused, accuracy, speed, duplicate }
  }, [samples])

  const handleExportCsv = () => {
    const { header, rows, filename } = buildWorkoutGpsTraceCsv(trace)
    downloadCsv(header, rows, filename)
  }

  if (samples.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
        No GPS samples were recorded for this workout.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="rounded bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">
            {counts.running} running
          </span>
          <span className="rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
            {counts.paused} paused
          </span>
          {counts.accuracy > 0 && (
            <span className="rounded bg-rose-100 px-2 py-0.5 font-medium text-rose-800">
              {counts.accuracy} low-accuracy rejects
            </span>
          )}
          {counts.speed > 0 && (
            <span className="rounded bg-rose-100 px-2 py-0.5 font-medium text-rose-800">
              {counts.speed} impossible-speed rejects
            </span>
          )}
          {counts.duplicate > 0 && (
            <span className="rounded bg-zinc-200 px-2 py-0.5 font-medium text-zinc-700">
              {counts.duplicate} duplicates
            </span>
          )}
          {trace.truncated && (
            <span className="rounded bg-amber-200 px-2 py-0.5 font-medium text-amber-900">
              Trace truncated to most recent samples
            </span>
          )}
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={showRejected}
              onChange={(e) => setShowRejected(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-border"
            />
            <span className="text-muted-foreground">Show rejected points</span>
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={followCursor}
              onChange={(e) => setFollowCursor(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-border"
            />
            <span className="text-muted-foreground">Follow cursor</span>
          </label>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TRACE_ASCENT_COLOR }} />
            climbing
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TRACE_FLAT_COLOR }} />
            flat
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TRACE_DESCENT_COLOR }} />
            descending
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TRACE_UNKNOWN_ELEVATION_COLOR }} />
            no altitude
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportCsv} className="w-fit gap-2">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <div className="h-[420px] overflow-hidden rounded-lg border border-border">
        <MapContainer
          center={initialCenter}
          zoom={14}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {!followCursor && <FitBounds bounds={bounds} />}
          {followCursor && <PanTo position={cursorPosition} />}

          {segments.map((segment, idx) => (
            <Polyline
              key={idx}
              positions={segment.positions}
              pathOptions={{
                color: segment.color,
                weight: 4,
                opacity: 0.85,
                dashArray: segment.state === 'paused' ? '6, 6' : undefined
              }}
            />
          ))}

          {showRejected &&
            rejectedSamples.map((sample, idx) => (
              <CircleMarker
                key={`rej-${idx}`}
                center={[sample.latitude, sample.longitude]}
                radius={3}
                pathOptions={{
                  color: REJECTED_COLOR,
                  fillColor: REJECTED_COLOR,
                  fillOpacity: 0.6,
                  weight: 1
                }}
              />
            ))}

          {cursorPosition && (
            <CircleMarker
              center={cursorPosition}
              radius={8}
              pathOptions={{
                color: '#0ea5e9',
                fillColor: '#0ea5e9',
                fillOpacity: 0.85,
                weight: 2
              }}
            />
          )}
        </MapContainer>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedIndex((i) => Math.max(0, i - 1))}
            className="gap-1"
            disabled={selectedIndex <= 0}
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>
          <input
            ref={sliderRef}
            type="range"
            min={0}
            max={Math.max(0, samples.length - 1)}
            defaultValue={selectedIndex}
            onChange={(e) => setSelectedIndex(Number(e.target.value))}
            className="h-2 flex-1 cursor-pointer appearance-none rounded-lg bg-muted"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedIndex((i) => Math.min(samples.length - 1, i + 1))}
            className="gap-1"
            disabled={selectedIndex >= samples.length - 1}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-center text-xs text-muted-foreground">
          Sample {selectedIndex + 1} of {samples.length}
        </p>
      </div>

      {selected && selectedInfo && <SampleDetails info={selectedInfo} />}
    </div>
  )
}

const formatSignedMeters = (value: number | null) => {
  if (value == null || !Number.isFinite(value)) return '—'
  const prefix = value > 0 ? '+' : ''
  return `${prefix}${value.toFixed(1)} m`
}

const SampleDetails = ({ info }: { info: ReturnType<typeof buildWorkoutGpsSampleInfos>[number] }) => {
  const sample = info.sample
  const stateBadge =
    sample.state === 'paused'
      ? 'bg-amber-100 text-amber-800'
      : 'bg-emerald-100 text-emerald-800'
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-border bg-muted/20 px-4 py-3 text-xs sm:grid-cols-4">
      <DetailCell label="State">
        <span className={`inline-flex rounded px-1.5 py-0.5 font-medium ${stateBadge}`}>
          {sample.state}
        </span>
        {!sample.accepted && (
          <span className="ml-1 inline-flex rounded bg-rose-100 px-1.5 py-0.5 font-medium text-rose-800">
            rejected{sample.rejectedReason ? `: ${sample.rejectedReason}` : ''}
          </span>
        )}
      </DetailCell>
      <DetailCell label="Time">{formatTimestamp(sample.timestamp)}</DetailCell>
      <DetailCell label="Lat / Lng">
        <span className="font-mono">
          {sample.latitude.toFixed(6)}, {sample.longitude.toFixed(6)}
        </span>
      </DetailCell>
      <DetailCell label="Source">{sample.source ?? '—'}</DetailCell>
      <DetailCell label="Accuracy">
        {sample.accuracyMeters != null ? `${sample.accuracyMeters.toFixed(1)} m` : '—'}
      </DetailCell>
      <DetailCell label="Speed">
        {sample.speedMps != null ? `${sample.speedMps.toFixed(2)} m/s` : '—'}
      </DetailCell>
      <DetailCell label="Altitude">
        {sample.altitudeMeters != null ? `${sample.altitudeMeters.toFixed(1)} m` : '—'}
      </DetailCell>
      <DetailCell label="Raw delta">{formatSignedMeters(info.elevationDeltaMeters)}</DetailCell>
      <DetailCell label="Accepted delta">{formatSignedMeters(info.acceptedElevationDeltaMeters)}</DetailCell>
      <DetailCell label="Accepted gain/loss">
        {info.acceptedCumulativeGainMeters.toFixed(1)} m / {info.acceptedCumulativeLossMeters.toFixed(1)} m
      </DetailCell>
      <DetailCell label="Accepted">{sample.accepted ? 'yes' : 'no'}</DetailCell>
    </div>
  )
}

const DetailCell = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-foreground">{children}</p>
  </div>
)
